import React, { useEffect, useState } from "react";
import { useToast } from "../../../../context/toast/toast-provider";
import commonStyles from "../../../style/common.module.css";
import BaseForm from "../../../../component/form/base-form";
import { BadgeAssignment } from "../../model/badge-assignment";
import { ModuleAssignment } from "../../model/module-assignment";
import { dismissStudentAssignment, submitStudentAssignment } from "../../service/assignment-service";
import { studentSubmitSelectFieldDefinitions } from "./student-submit-select-field-definition";
import { studentSubmitFieldDefinitions } from "./student-submit-field-definition";
import { Alert, AlertTitle, Box } from "@mui/material";
import { ASSIGNMENT_QUERY_PARAMETER_TAB_VALUE_STUDENT_SUBMIT } from "../../utils/assignment-constants";
import { useSearchParams } from "react-router-dom";
import { getBadgeTerminology, getModuleCourseLevel, getModuleTerminology, getGeneralCourseLevel } from "../../utils/terminology";
import { CourseLevel } from "../../../-education/enum/course-level";
import { updateSearchParams } from "../../utils/assignment-navigation";
import { AssignmentFilter, getFilter, getSelectedBadge, getUnsubmittedBadgeSelectItems } from "../utils/student-assignment-tools";
import { getModuleSelectItems } from "../../utils/assignment-auto-select";
import { StudentGroup } from "../../../-education/model/stream";
import { getMyGroup } from "../../../-education/service/student-group-service";
import PresentationViewer from "../../../../component/presentation/presentation-viewer";

export default function StudentSubmit({
    generalCourseLevel,
    unsubmittedModuleAssignments,
    updateModuleAssignments,
    enableDismiss
}: {
        generalCourseLevel: CourseLevel,
        unsubmittedModuleAssignments: ModuleAssignment[],
        updateModuleAssignments(badgeAssignment: BadgeAssignment): void,
        enableDismiss: boolean
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [filter, setFilter] = useState<AssignmentFilter | undefined>();
    const [filterInitiated, setFilterInitiated] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const [submitBadgeAssignment, setSubmitBadgeAssignment] = useState<BadgeAssignment>({ workingBadgePath: "" });
    const [myGroup, setMyGroup] = useState<StudentGroup | null>(null);

    const toast = useToast();
    const studentCourseLevel = unsubmittedModuleAssignments.length > 0 ? getGeneralCourseLevel(unsubmittedModuleAssignments) : generalCourseLevel;

    useEffect(() => {
        if (generalCourseLevel === CourseLevel.LG_High_School) {
            getMyGroup()
                .then(group => setMyGroup(group))
                .catch(() => setMyGroup(null));
        }
    }, [generalCourseLevel]);

    useEffect(() => {
        if (filterInitiated === false) {
            setFilter(getFilter(unsubmittedModuleAssignments, searchParams));
            setFilterInitiated(true);
        }
    }, [filterInitiated, searchParams, unsubmittedModuleAssignments]);

    useEffect(() => {
        const selectedBadge = unsubmittedModuleAssignments.flatMap(_ => _.badges).find(_ => _.badgeId === filter?.badgeId);

        if (selectedBadge !== undefined && selectedBadge.moduleId === filter?.moduleId) {
            setSubmitBadgeAssignment({ ...selectedBadge, workingBadgePath: "" });
        } else {
            setSubmitBadgeAssignment({ workingBadgePath: "" });
        }
    }, [unsubmittedModuleAssignments, filter]);

    useEffect(() => {
        const selectedModule = unsubmittedModuleAssignments.find(_ => _.moduleId === filter?.moduleId);
        if (selectedModule !== undefined && selectedModule.moduleId !== filter?.moduleId) {
            let selectedBadge = getSelectedBadge(selectedModule);
            setFilter(filter => ({ ...filter, badgeId: selectedBadge?.badgeId }));
        }
    }, [unsubmittedModuleAssignments, filter?.moduleId]);

    useEffect(() => {
        updateSearchParams(searchParams, setSearchParams, submitBadgeAssignment, ASSIGNMENT_QUERY_PARAMETER_TAB_VALUE_STUDENT_SUBMIT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitBadgeAssignment]);

    const getSubmitSelectFieldDefinitions = () => {
        // Module
        const modules = getModuleSelectItems(unsubmittedModuleAssignments)
        const selectedModule = unsubmittedModuleAssignments?.find(_ => _.moduleId === filter?.moduleId);

        if (filter?.moduleId !== undefined && selectedModule === undefined) {
            setFilter({ ...filter, moduleId: undefined });
        }

        studentSubmitSelectFieldDefinitions.find(_ => _.id === "moduleId")!.name = getModuleTerminology(selectedModule?.moduleCourseLevelId ?? studentCourseLevel);
        studentSubmitSelectFieldDefinitions.find(_ => _.id === "moduleId")!.values = modules;
        studentSubmitSelectFieldDefinitions.find(_ => _.id === "badgeId")!.disabled = true;

        // Badge
        if (selectedModule !== undefined) {
            const badges = getUnsubmittedBadgeSelectItems(selectedModule);

            if (filter?.badgeId !== undefined && badges?.find(_ => _.id === filter.badgeId) === undefined) {
                setFilter({ ...filter, badgeId: undefined });
            }

            studentSubmitSelectFieldDefinitions.find(_ => _.id === "badgeId")!.name = getBadgeTerminology(selectedModule?.moduleCourseLevelId ?? studentCourseLevel);
            studentSubmitSelectFieldDefinitions.find(_ => _.id === "badgeId")!.values = badges;
            studentSubmitSelectFieldDefinitions.find(_ => _.id === "badgeId")!.disabled = false;
        }

        return studentSubmitSelectFieldDefinitions;
    }

    const getSubmitFieldDefinitions = () => {
        studentSubmitFieldDefinitions.find(_ => _.id === "workingBadgePath")!.name = `${getBadgeTerminology(getModuleCourseLevel(unsubmittedModuleAssignments, submitBadgeAssignment.badgeId))} path`;
        studentSubmitFieldDefinitions.find(_ => _.id === "workingBadgePath")!.disabled = false;

        return studentSubmitFieldDefinitions;
    }

    const handleSubmit = (badgeAssignment: BadgeAssignment) => {
        const badgeAssignmentRequest = { ...badgeAssignment };
        const moduleCourseLevel = getModuleCourseLevel(unsubmittedModuleAssignments, badgeAssignment.badgeId);
        const hasWorkingBadgePath = badgeAssignmentRequest.workingBadgePath !== undefined && badgeAssignmentRequest.workingBadgePath.replaceAll(' ', '').length !== 0;

        if (!hasWorkingBadgePath) {
            toast.addToast(`Please provide a ${getBadgeTerminology(moduleCourseLevel)} path before submitting`, "error");
            return;
        }

        setSubmitting(true);

        if (badgeAssignmentRequest.comment !== undefined && badgeAssignmentRequest.comment.length > 0) {
            badgeAssignmentRequest.comments = [...(badgeAssignment.comments ?? [])];
            badgeAssignmentRequest.comments.push(`<sc>${badgeAssignment.comment}`);
        }

        submitStudentAssignment(badgeAssignmentRequest).then(result => {
            toast.addToast(`Great work! ${getBadgeTerminology(moduleCourseLevel)} ${badgeAssignment.badgeName} has been submitted`, "success");

            result.moduleId = badgeAssignmentRequest.moduleId;
            updateModuleAssignments(result);

            setSubmitBadgeAssignment({ workingBadgePath: "" });
            setFilter({ ...filter, badgeId: undefined });
            setSubmitting(false);
        }).catch(() => {
            toast.addToast(`Unable to submit ${getBadgeTerminology(moduleCourseLevel)} ${badgeAssignment.badgeName}`, "error");
            setSubmitting(false);
        });
    }

    const handleDismiss = () => {
        setDismissing(true);
        const moduleCourseLevel = getModuleCourseLevel(unsubmittedModuleAssignments, submitBadgeAssignment.badgeId);

        dismissStudentAssignment(submitBadgeAssignment.badgeId!).then(result => {
            
            toast.addToast(`${getBadgeTerminology(moduleCourseLevel)} ${submitBadgeAssignment.badgeName} has been dismissed`, "success");

            result.moduleId = submitBadgeAssignment.moduleId;
            updateModuleAssignments(result);

            setSubmitBadgeAssignment({ workingBadgePath: "" });
            setFilter({ ...filter, badgeId: undefined });
            setDismissing(false);
        }).catch(() => {
            toast.addToast(`Unable to dismiss ${getBadgeTerminology(moduleCourseLevel)} ${submitBadgeAssignment.badgeName}`, "error");
            setDismissing(false);
        });
    }

    const selectedModuleNoEvaluator = () => {
        return unsubmittedModuleAssignments.find(_ => 
            _.moduleId === filter?.moduleId && 
            _.isEvaluatorAvailable === false);
    }

    const selectedBadgeNoEvaluator = () => {
        return unsubmittedModuleAssignments
            .find(_ => _.moduleId === filter?.moduleId && _.isEvaluatorAvailable === true)?.badges
            .find(_ => _.badgeId === filter?.badgeId && _.isEvaluatorAvailable === false);
    }

    const showEvaluatorWarning = () => {
        const moduleNoEvaluator = selectedModuleNoEvaluator();
        const badgeNoEvaluator = selectedBadgeNoEvaluator();

        if (moduleNoEvaluator !== undefined) {
            return <div>
                <Alert severity="warning" className={commonStyles.alert} >
                    <span>
                        There are currently no active <b>evaluators</b> available to <b>review</b> a submitted&nbsp;
                        {getBadgeTerminology(moduleNoEvaluator.moduleCourseLevelId, true)} for&nbsp;
                        {getModuleTerminology(moduleNoEvaluator.moduleCourseLevelId)} <b>{moduleNoEvaluator.moduleName}</b>.
                        You can still submit, but review may be delayed.
                    </span>
                </Alert>
            </div>
        } else if (badgeNoEvaluator !== undefined) {
            var selectModule = unsubmittedModuleAssignments.find(_ => _.moduleId === filter?.moduleId);
            return <div>
                <Alert severity="warning" className={commonStyles.alert} >
                    <span>
                        There are currently no active <b>evaluators</b> available to <b>review</b> a submitted&nbsp;
                        <b>{badgeNoEvaluator.badgeName}</b>&nbsp;
                        {getBadgeTerminology(selectModule?.moduleCourseLevelId)}.
                        You can still submit, but review may be delayed.
                    </span>
                </Alert>
            </div>
        }
    }

    return (
        <React.Fragment>
            {myGroup && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Group Submission</AlertTitle>
                    You are submitting for <strong>{myGroup.name}</strong> ({myGroup.members} members).
                    This submission will apply to all group members.
                    {myGroup.groupEmail && (
                        <Box sx={{ mt: 1 }}>
                            Group email: <strong>{myGroup.groupEmail}</strong>
                        </Box>
                    )}
                </Alert>
            )}
            {unsubmittedModuleAssignments.length > 0 && filter !== undefined && <BaseForm
                model={filter}
                setModel={setFilter}
                fields={getSubmitSelectFieldDefinitions()}
            />}
            {submitBadgeAssignment.badgeId !== undefined && <BaseForm
                model={submitBadgeAssignment}
                setModel={setSubmitBadgeAssignment}
                fields={getSubmitFieldDefinitions()}
                submitEntity={handleSubmit}
                submitting={submitting}
                submitButtonText='Submit'
                deleteEntity={enableDismiss ? handleDismiss : undefined}
                deleting={dismissing}
                deleteButtonText='Dismiss'
                deleteConfirmationText={`Are you sure you want to dismiss 
                    ${getBadgeTerminology(getModuleCourseLevel(unsubmittedModuleAssignments, submitBadgeAssignment.badgeId))} <b>
                    ${submitBadgeAssignment.badgeName}</b>?`}
            />}
            {submitBadgeAssignment.badgeId !== undefined && (submitBadgeAssignment.presentationUrl || submitBadgeAssignment.sessionMapUrl) && (
                <PresentationViewer
                    badgeName={submitBadgeAssignment.badgeName ?? ''}
                    presentationUrl={submitBadgeAssignment.presentationUrl}
                    sessionMapUrl={submitBadgeAssignment.sessionMapUrl}
                    showSessionMap={false}
                />
            )}
            {showEvaluatorWarning()}
            {unsubmittedModuleAssignments.length === 0 && <div>
                <span>
                    You have no&nbsp;
                    {getBadgeTerminology(studentCourseLevel)} to <b>Submit</b>. You can look at your previous&nbsp;
                    {getBadgeTerminology(studentCourseLevel)} submissions under your <b>Journal</b>.
                </span>
            </div>}
        </React.Fragment>
    );
}
